import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useParams,
  useSearchParams,
} from "react-router-dom";

import SnapsWise from "../components/SnapsWise";

const basename = (value) => {
  if (typeof value === "string") {
    return value.split("/").pop() || value;
  }

  if (value && typeof value === "object") {
    return basename(value.file || value.full_path || value.path || "");
  }

  return "";
};

const getTranscriptFilename = (transcript) => {
  if (!transcript) return "";

  return basename(
    transcript.file ||
      transcript.full_path ||
      transcript.path ||
      ""
  );
};

const SubjectDeepLink = () => {
  const { subjectId } = useParams();
  const [searchParams] = useSearchParams();

  const requestedFile = searchParams.get("file");

  const [subject, setSubject] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const [loadingSubject, setLoadingSubject] = useState(true);
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem("token");

    return token
      ? { Authorization: `Bearer ${token}` }
      : {};
  };

  /*
   * Load the MongoDB subject document.
   *
   * Expected endpoint:
   * GET /subjects/by-id/:subjectId
   */
  useEffect(() => {
    if (!subjectId) {
      setError("No subject ID was provided.");
      setLoadingSubject(false);
      return;
    }

    const controller = new AbortController();

    const loadSubject = async () => {
      setLoadingSubject(true);
      setError(null);
      setSubject(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_USER_API_URL}/subjects/by-id/${encodeURIComponent(
            subjectId
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
            signal: controller.signal,
          }
        );

        if (response.status === 404) {
          throw new Error("This audiobook could not be found.");
        }

        if (response.status === 401 || response.status === 403) {
          throw new Error("You do not have access to this audiobook.");
        }

        if (!response.ok) {
          throw new Error(`Could not load audiobook (${response.status}).`);
        }

        const json = await response.json();

        // Supports either:
        // { subject: {...} }
        // or the subject object directly.
        const loadedSubject = json?.subject || json;

        if (!loadedSubject) {
          throw new Error("The server returned no subject.");
        }

        if (
          loadedSubject.type &&
          loadedSubject.type.toLowerCase() !== "audiobook"
        ) {
          throw new Error("This shared item is not an audiobook.");
        }

        setSubject(loadedSubject);
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("SubjectDeepLink: subject load failed", err);
        setError(err.message || "Could not load the audiobook.");
      } finally {
        if (!controller.signal.aborted) {
          setLoadingSubject(false);
        }
      }
    };

    loadSubject();

    return () => controller.abort();
  }, [subjectId]);

  /*
   * Load the files belonging to the subject using your existing endpoint.
   */
  useEffect(() => {
    if (!subject) return;

    const controller = new AbortController();

    const loadTranscripts = async () => {
      setLoadingTranscripts(true);
      setError(null);
      setTranscripts([]);

      try {
        const payload = {
          owner: subject.owner,
          author: subject.author,
          title: subject.title,
          language: subject.language,
          type: subject.type,
        };

        const response = await fetch(
          `${process.env.REACT_APP_USER_API_URL}/subjects/get-structure`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
            body: JSON.stringify(payload),
            signal: controller.signal,
          }
        );

        if (response.status === 401 || response.status === 403) {
          throw new Error("You do not have access to this audiobook.");
        }

        if (!response.ok) {
          throw new Error(
            `Could not load audiobook files (${response.status}).`
          );
        }

        const json = await response.json();

        const loadedTranscripts = Array.isArray(json)
          ? json
          : Array.isArray(json?.transcripts)
            ? json.transcripts
            : Array.isArray(json?.files)
              ? json.files
              : [];

        if (loadedTranscripts.length === 0) {
          throw new Error("This audiobook has no playable files.");
        }

        setTranscripts(loadedTranscripts);

        if (requestedFile) {
          const decodedRequestedFile = decodeURIComponent(requestedFile);

          const requestedIndex = loadedTranscripts.findIndex((item) => {
            const filename = getTranscriptFilename(item);

            return (
              filename === decodedRequestedFile ||
              filename === basename(decodedRequestedFile) ||
              item?.file === decodedRequestedFile
            );
          });

          setActiveIndex(requestedIndex >= 0 ? requestedIndex : 0);
        } else {
          setActiveIndex(0);
        }
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("SubjectDeepLink: structure load failed", err);
        setError(err.message || "Could not load the audiobook files.");
      } finally {
        if (!controller.signal.aborted) {
          setLoadingTranscripts(false);
        }
      }
    };

    loadTranscripts();

    return () => controller.abort();
  }, [subject, requestedFile]);

  const currentFile = transcripts[activeIndex] || null;

  const sectionTitle = useMemo(() => {
    if (!currentFile) return subject?.title || "Audiobook";

    return (
      currentFile.uiTitle ||
      currentFile.title ||
      `Part ${activeIndex + 1}`
    );
  }, [currentFile, activeIndex, subject?.title]);

  if (loadingSubject || loadingTranscripts) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-lg">
          <div className="font-semibold text-slate-800">
            Loading audiobook…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <div className="w-full max-w-lg rounded-2xl border border-red-200 bg-white p-6 shadow-lg">
          <h1 className="text-xl font-bold text-slate-900">
            Audiobook unavailable
          </h1>

          <p className="mt-2 text-sm text-slate-600">{error}</p>

          <Link
            to="/"
            className="mt-5 inline-flex rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Back to Library
          </Link>
        </div>
      </div>
    );
  }

  if (!subject || !currentFile) {
    return null;
  }

  return (
    <div className="flex h-screen min-h-0 flex-col bg-slate-100">
      <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-2 shadow-sm">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/"
            className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            ← Library
          </Link>

          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-slate-900">
              {subject.title}
            </div>

            <div className="truncate text-xs text-slate-500">
              {sectionTitle}
            </div>
          </div>
        </div>

        {transcripts.length > 1 && (
          <select
            value={activeIndex}
            onChange={(event) => {
              setActiveIndex(Number(event.target.value));
            }}
            className="max-w-[45vw] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
            aria-label="Choose audiobook part"
          >
            {transcripts.map((item, index) => (
              <option
                key={`${getTranscriptFilename(item)}-${index}`}
                value={index}
              >
                {item.uiTitle ||
                  item.title ||
                  `Part ${index + 1}`}
              </option>
            ))}
          </select>
        )}
      </header>

      <main className="min-h-0 flex-1">
        <SnapsWise
          key={`${subjectId}-${getTranscriptFilename(currentFile)}`}
          subject={subject}
          file={currentFile}
          sectionTitle={sectionTitle}
        />
      </main>
    </div>
  );
};

export default SubjectDeepLink;